import {useEffect, useRef, useCallback} from 'react';
import {useAudioRecorder} from '@Components/ai-prompt-box/hooks/useAudioRecorder';
import {useSelector} from 'react-redux';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {transcribeAudioToText, AudioLimitError} from '@Libraries/speech-to-text.library';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {useAppDispatch} from '@/hooks';
import {setAudioLimitState, updateAudioLimitAfterSend} from '@/ai-chat/ai-chat.reducer';
import type {RootState} from '@/store';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {openAiChatAuthenticateModal} from '@Libraries/ai-chat.library';
import {getMicLoginCallback, registerStartRecording, unregisterStartRecording} from '@Components/ai-prompt-box/libraries/ai-prompt-box-mic.library';

const MAX_RECORDING_DURATION_MS = 30_000;

export interface UseAiRecordAudioReturn {
  analyserNode: AnalyserNode | null;
  isRecording: boolean;
  isProcessing: boolean;
  startRecording: () => void;
  stopAndSubmit: () => void;
  cancel: () => void;
}

enum PermissionState {
  DENIED = 'denied',
  GRANTED = 'granted',
  PROMPT = 'prompt',
  NOT_SUPPORTED = 'not supported',
}

const getPermissionState = async (permissionName: string): Promise<PermissionState> => {
  try {
    const result = await navigator.permissions.query({name: permissionName as PermissionName});
    return result.state as PermissionState;
  } catch {
    return PermissionState.NOT_SUPPORTED;
  }
};

export const useAiRecordAudio = (promptBoxId: string): UseAiRecordAudioReturn => {
  const audioRecorder = useAudioRecorder();
  const audioRecorderRef = useRef(audioRecorder);
  audioRecorderRef.current = audioRecorder;
  const {state, actions} = useAiPromptBox(promptBoxId);
  const dispatch = useAppDispatch();
  const originalPlaceholderRef = useRef(state.placeholder);
  const originalPromptRef = useRef(state.prompt);
  const isProcessingRef = useRef(false);
  const isCancelledRef = useRef(false);
  const recordingDurationMsRef = useRef(0);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isRecording = state.isRecordingAudio ?? false;
  const isProcessing = state.isProcessingAudio ?? false;
  const isActiveRef = useRef(false);
  const permissionDenied = state.transcription?.permissionDenied ?? false;
  const isAudioLimitReached = useSelector((s: RootState) => s.aiChat.audioLimit.isLimitReached);
  const isAudioLimitLoading = useSelector((s: RootState) => s.aiChat.isFetchingAudioLimit || s.aiChat.audioLimit.lastSyncedAt === null);

  const checkPermission = useCallback(async (): Promise<void> => {
    const permission = await getPermissionState('microphone');
    actions.update({transcription: {permissionDenied: permission === PermissionState.DENIED}});
  }, []);

  useEffect(() => {
    void checkPermission();
  }, [checkPermission]);

  useEffect(() => {
    if (audioRecorder.isPermissionDenied) {
      actions.update({transcription: {permissionDenied: true}});
      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }
      isCancelledRef.current = true;
      actions.update({
        prompt: originalPromptRef.current,
        isRecordingAudio: false,
        isProcessingAudio: false,
        placeholder: originalPlaceholderRef.current,
      });
    } else {
      actions.update({transcription: {permissionDenied: false}});
    }
  }, [audioRecorder.isPermissionDenied]);

  const shouldProcessBlobRef = useRef(false);

  const handleProcessing = async (blob: Blob): Promise<void> => {
    trackGA4CustomEvent(GA4EventName.AICHAT_VOICE_END, {
      [GA4EventParamName.VALUE]: String(Math.round(recordingDurationMsRef.current / 1000)),
    });
    isProcessingRef.current = true;
    actions.update({
      isRecordingAudio: false,
      isProcessingAudio: true,
      placeholder: window.i18next.t('pmwjs_processing_your_audio_ellipses'),
    });

    try {
      const result = await transcribeAudioToText(blob);
      if (result.audioLimit) {
        dispatch(updateAudioLimitAfterSend(result.audioLimit));
        if (result.audioLimit.remaining === 0) {
          trackGA4CustomEvent(GA4EventName.AICHAT_VOICE_LIMIT);
        }
      }
      if (isCancelledRef.current) {
        return;
      }
      const existingPrompt = originalPromptRef.current.trim();
      const newPrompt = existingPrompt ? `${existingPrompt} ${result.text}` : result.text;
      actions.update({
        prompt: newPrompt,
        promptBoxState: PromptBoxState.ACTIVE,
        isProcessingAudio: false,
        placeholder: originalPlaceholderRef.current,
      });
    } catch (e) {
      if (isCancelledRef.current) {
        return;
      }
      if (e instanceof AudioLimitError) {
        dispatch(setAudioLimitState(e.audioLimit));
        trackGA4CustomEvent(GA4EventName.AICHAT_VOICE_LIMIT);
      }
      actions.update({
        prompt: originalPromptRef.current,
        isProcessingAudio: false,
        placeholder: originalPlaceholderRef.current,
      });
    } finally {
      isProcessingRef.current = false;
      isActiveRef.current = false;
    }
  };

  if (audioRecorder.recordedBlob && shouldProcessBlobRef.current && !isProcessingRef.current) {
    shouldProcessBlobRef.current = false;
    void handleProcessing(audioRecorder.recordedBlob);
  }

  const startRecording = useCallback((): void => {
    if (!isUserLoggedIn()) {
      void openAiChatAuthenticateModal(getMicLoginCallback(promptBoxId) ?? (() => {}));
      return;
    }

    if (!isUserPremium()) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_AUDIO_INPUT as string);
      return;
    }

    if (permissionDenied || isAudioLimitReached || isAudioLimitLoading) {
      return;
    }

    originalPlaceholderRef.current = state.placeholder;
    originalPromptRef.current = state.prompt;
    isCancelledRef.current = false;
    isActiveRef.current = true;
    actions.update({
      prompt: '',
      isRecordingAudio: true,
      placeholder: window.i18next.t('pmwjs_say_something_ellipses'),
    });
    void audioRecorder.startRecording();
    trackGA4CustomEvent(GA4EventName.AICHAT_VOICE_START);

    maxDurationTimerRef.current = setTimeout(() => {
      recordingDurationMsRef.current = MAX_RECORDING_DURATION_MS;
      shouldProcessBlobRef.current = true;
      audioRecorderRef.current.stopRecording();
    }, MAX_RECORDING_DURATION_MS);
  }, [permissionDenied, isAudioLimitReached, isAudioLimitLoading, state.placeholder, state.prompt, actions, audioRecorder, promptBoxId]);

  const stopAndSubmit = useCallback((): void => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    recordingDurationMsRef.current = audioRecorder.recordingTime;
    shouldProcessBlobRef.current = true;
    audioRecorder.stopRecording();
  }, [audioRecorder]);

  const cancel = useCallback((): void => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    isCancelledRef.current = true;
    isActiveRef.current = false;
    audioRecorder.stopRecording();
    isProcessingRef.current = false;
    actions.update({
      prompt: originalPromptRef.current,
      isRecordingAudio: false,
      isProcessingAudio: false,
      placeholder: originalPlaceholderRef.current,
    });
  }, [audioRecorder, actions]);

  const startRecordingRef = useRef(startRecording);
  startRecordingRef.current = startRecording;

  useEffect(() => {
    registerStartRecording(promptBoxId, startRecordingRef);
    return (): void => {
      unregisterStartRecording(promptBoxId);
    };
  }, [promptBoxId]);

  useEffect(() => {
    return (): void => {
      if (!isActiveRef.current) {
        return;
      }

      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }
      isCancelledRef.current = true;
      audioRecorderRef.current.stopRecording();
      isProcessingRef.current = false;
      actions.update({
        prompt: originalPromptRef.current,
        isRecordingAudio: false,
        isProcessingAudio: false,
        placeholder: originalPlaceholderRef.current,
      });
    };
  }, [promptBoxId]);

  return {
    analyserNode: audioRecorder.analyserNode,
    isRecording,
    isProcessing,
    startRecording,
    stopAndSubmit,
    cancel,
  };
};
